import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { MatButton } from '@angular/material/button';
import {
  MatButtonToggle,
  MatButtonToggleGroup,
} from '@angular/material/button-toggle';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { TranslatePipe } from '@ngx-translate/core';
import { Headline } from '@spirit-testing/component-library';

import { CvSectionReviewComponent } from './cv-section-review.component';
import { ModalComponent } from '../../shared/components/modal/modal.component';
import { buildReviewSections, isEntryFilled, serializeFields } from './cv-import-forms';
import { CvReviewSection } from './cv-import-review.model';
import { CvImportService } from '../../services/cv-import.service';
import { BeraterService } from '../../services/berater.service';
import { MessageService } from '../../shared/components/message.service';
import { IBerater } from '../../shared/model/berater.model';
import {
  CvApplyRequest,
  CvApplyResult,
  CvImportLanguage,
  DEFAULT_CV_IMPORT_LANGUAGE,
} from '../../shared/model/cv-import.model';
import {
  ADMIN_DASH_RETURN_MARKER,
  ADMIN_DASH_RETURN_QUERY_PARAM,
} from '../../shared/constants/cv-import-return';

const ALLOWED_EXTENSIONS = ['.docx', '.pdf', '.pptx'];
const MAX_CV_SIZE_MB = 10;

/**
 * cv-extract reports the PDF (NP20-923) and PowerPoint (NP20-942) failure modes as
 * 422 and separates them only through the Problem+JSON `type` URI. Matched by
 * suffix, like the other problem-type mappings in the app.
 */
const EXTRACTION_PROBLEM_KEYS: [suffix: string, messageKey: string][] = [
  ['/cv-pdf-protected', 'cvImport.errors.pdfProtected'],
  ['/cv-pdf-no-text', 'cvImport.errors.pdfNoText'],
  ['/cv-pdf-unreadable', 'cvImport.errors.pdfUnreadable'],
  ['/cv-pptx-protected', 'cvImport.errors.pptxProtected'],
  ['/cv-pptx-no-text', 'cvImport.errors.pptxNoText'],
  ['/cv-pptx-unreadable', 'cvImport.errors.pptxUnreadable'],
];

/**
 * Fallback message per upload format, for a 422 that carries no known problem type.
 * Keeps a PDF or a deck from producing the .docx wording.
 */
const UNREADABLE_KEYS_BY_EXTENSION: [extension: string, messageKey: string][] = [
  ['.pdf', 'cvImport.errors.pdfUnreadable'],
  ['.pptx', 'cvImport.errors.pptxUnreadable'],
];

type UploadState = 'idle' | 'loading' | 'error';

@Component({
  selector: 'app-cv-import-wizard',
  templateUrl: './cv-import-wizard.component.html',
  styleUrl: './cv-import-wizard.component.scss',
  imports: [
    MatStepperModule,
    MatButton,
    MatButtonToggle,
    MatButtonToggleGroup,
    MatIcon,
    MatProgressSpinner,
    TranslatePipe,
    Headline,
    CvSectionReviewComponent,
    ModalComponent,
  ],
  standalone: true,
})
export class CvImportWizardComponent implements OnInit {
  @ViewChild('stepper') stepper?: MatStepper;

  consultantId = '';
  berater: IBerater | null = null;
  sections: CvReviewSection[] = [];

  selectedFile: File | null = null;
  uploadState: UploadState = 'idle';
  /** Gilt nur fuer den laufenden Import; nichts davon wird gespeichert (NP20-1071). */
  selectedLanguage: CvImportLanguage = DEFAULT_CV_IMPORT_LANGUAGE;
  readonly languageOptions: readonly CvImportLanguage[] = ['de', 'en'];
  applying = false;
  /** Set after cv-apply; carries what the lenient import skipped / flagged. */
  applyResult: CvApplyResult | null = null;
  /** Shows the result modal when the apply dropped entries or needs a review. */
  showResultModal = false;

  readonly maxSectionEntries = 100;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private cvImportService: CvImportService,
    private beraterService: BeraterService,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef,
  ) {}

  private get profileRoute(): string[] {
    return ['profile', this.consultantId];
  }

  ngOnInit(): void {
    this.consultantId = this.route.snapshot.paramMap.get('consultantId') ?? '';
    if (this.consultantId) {
      this.beraterService.find(this.consultantId).subscribe({
        next: (response) => (this.berater = response.body),
      });
    }
  }

  get targetName(): string {
    if (!this.berater) {
      return '';
    }
    return `${this.berater.vorname ?? ''} ${this.berater.nachname ?? ''}`.trim();
  }

  get hasDraft(): boolean {
    return this.sections.length > 0;
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) {
      return;
    }

    const name = file.name.toLowerCase();
    if (!ALLOWED_EXTENSIONS.some((extension) => name.endsWith(extension))) {
      // Das alte Binaerformat ist kein beliebiges Fremdformat: die Datei sieht wie eine
      // unterstuetzte Praesentation aus, deshalb sagt die Meldung, was zu tun ist. Fuer
      // jede andere Ablehnung waere der Satz nur Rauschen.
      this.messageService.warning(
        name.endsWith('.ppt')
          ? 'cvImport.errors.legacyPowerPoint'
          : 'cvImport.errors.unsupportedFormat',
      );
      return;
    }
    if (file.size > MAX_CV_SIZE_MB * 1024 * 1024) {
      this.messageService.warning('cvImport.errors.tooLarge');
      return;
    }
    this.selectedFile = file;
    this.uploadState = 'idle';
  }

  removeFile(event: Event, input: HTMLInputElement): void {
    event.stopPropagation();
    this.selectedFile = null;
    input.value = '';
  }

  /**
   * Wirkt erst beim naechsten Extraktionslauf; ein bereits geladener Entwurf bleibt
   * unberuehrt. Waehrend der Extraktion sperrt das [disabled]-Binding der Gruppe die
   * Auswahl — ein zusaetzlicher Guard hier wuerde die einwegige [value]-Bindung von
   * der Anzeige entkoppeln.
   */
  setLanguage(language: CvImportLanguage): void {
    this.selectedLanguage = language;
  }

  startExtraction(): void {
    if (!this.selectedFile || this.uploadState === 'loading') {
      return;
    }

    this.uploadState = 'loading';
    this.cvImportService
      .importCv(this.consultantId, this.selectedFile, this.selectedLanguage)
      .subscribe({
        next: (draft) => {
          this.sections = buildReviewSections(draft);
          this.uploadState = 'idle';
          if (this.sections.length === 0) {
            this.messageService.info('cvImport.errors.noData');
            return;
          }
          // Flush the [completed] binding before advancing the linear stepper.
          this.cdr.detectChanges();
          this.stepper?.next();
        },
        error: (err: HttpErrorResponse) => {
          this.uploadState = 'error';
          this.messageService.error(this.extractionErrorKey(err));
        },
      });
  }

  acceptedCount(section: CvReviewSection): number {
    return section.entries.filter((entry) => entry.accepted).length;
  }

  get totalAccepted(): number {
    return this.sections.reduce(
      (sum, section) => sum + this.acceptedCount(section),
      0,
    );
  }

  get hasInvalidAccepted(): boolean {
    return this.sections.some((section) =>
      section.entries.some((entry) => entry.accepted && entry.form.invalid),
    );
  }

  get hasTooManyEntries(): boolean {
    return this.sections.some(
      (section) =>
        !section.single &&
        this.acceptedCount(section) > this.maxSectionEntries,
    );
  }

  get canApply(): boolean {
    return (
      !this.applying &&
      this.applyResult === null &&
      this.totalAccepted > 0 &&
      !this.hasInvalidAccepted &&
      !this.hasTooManyEntries
    );
  }

  apply(): void {
    if (!this.canApply) {
      return;
    }

    this.applying = true;
    this.cvImportService
      .apply(this.consultantId, this.buildApplyPayload())
      .subscribe({
        next: (result) => {
          this.applying = false;
          this.applyResult = result;
          if (this.isCleanResult(result)) {
            this.messageService.success('cvImport.success');
            this.navigateBack();
          } else {
            this.showResultModal = true;
          }
        },
        error: (err: HttpErrorResponse) => {
          this.applying = false;
          this.messageService.error(this.applyErrorKey(err));
        },
      });
  }

  /** The lenient import took everything and nothing needs a follow-up correction. */
  private isCleanResult(result: CvApplyResult): boolean {
    return (
      !result.needsReview &&
      result.skippedSkills.length === 0 &&
      result.skippedBranches.length === 0 &&
      result.skippedSchwerpunkte.length === 0 &&
      result.skippedDuplicates === 0
    );
  }

  get hasSkipped(): boolean {
    const result = this.applyResult;
    return (
      !!result &&
      (result.skippedSkills.length > 0 ||
        result.skippedBranches.length > 0 ||
        result.skippedSchwerpunkte.length > 0 ||
        result.skippedDuplicates > 0)
    );
  }

  finish(): void {
    this.showResultModal = false;
    if (this.applyResult?.needsReview) {
      this.router.navigate(this.profileRoute);
      return;
    }
    this.navigateBack();
  }

  cancel(): void {
    this.navigateBack();
  }

  private navigateBack(): void {
    const from = this.route.snapshot.queryParamMap.get(ADMIN_DASH_RETURN_QUERY_PARAM);
    if (from === ADMIN_DASH_RETURN_MARKER) {
      this.router.navigate(['administration']);
      return;
    }
    this.router.navigate(this.profileRoute);
  }

  /** Collects the accepted entries into a single cv-apply payload. */
  private buildApplyPayload(): CvApplyRequest {
    const payload: Record<string, unknown> = {};

    for (const section of this.sections) {
      const accepted = section.entries.filter((entry) => entry.accepted);
      if (accepted.length === 0) {
        continue;
      }

      if (section.single) {
        payload[section.key] = this.dropEmpty(
          serializeFields(section.fields, accepted[0].form),
        );
      } else {
        // Drop accepted-but-blank entries so empty rows don't land in the profile.
        const entries = accepted
          .map((entry) => serializeFields(section.fields, entry.form))
          .filter((entry) => isEntryFilled(entry));
        if (entries.length === 0) {
          continue;
        }
        payload[section.key] = entries;
      }
    }

    return payload as CvApplyRequest;
  }

  /** Removes null scalars so empty CV fields don't overwrite existing data. */
  private dropEmpty(obj: Record<string, unknown>): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value !== null) {
        out[key] = value;
      }
    }
    return out;
  }

  private applyErrorKey(err: HttpErrorResponse): string {
    switch (err.status) {
      case 409:
        return 'cvImport.errors.projectExists';
      case 404:
        return 'cvImport.errors.notFound';
      case 400:
        return 'cvImport.errors.capExceeded';
      default:
        return 'cvImport.errors.applyFailed';
    }
  }

  private extractionErrorKey(err: HttpErrorResponse): string {
    if ((err.url ?? '').includes('cv-structure')) {
      return err.status === 400
        ? 'cvImport.errors.textInvalid'
        : 'cvImport.errors.aiFailed';
    }
    switch (err.status) {
      case 415:
        return 'cvImport.errors.unsupportedFormat';
      case 413:
        return 'cvImport.errors.tooLarge';
      case 422:
        return this.unprocessableErrorKey(err);
      case 400:
        return 'cvImport.errors.emptyFile';
      default:
        return 'cvImport.errors.extractFailed';
    }
  }

  /**
   * Falls back to a message naming the format that was actually uploaded, so a PDF or
   * a deck never produces the .docx wording even when the response carries no problem
   * type.
   */
  private unprocessableErrorKey(err: HttpErrorResponse): string {
    const problemType = (err.error as { type?: unknown } | null)?.type;
    if (typeof problemType === 'string') {
      const match = EXTRACTION_PROBLEM_KEYS.find(([suffix]) =>
        problemType.endsWith(suffix),
      );
      if (match) {
        return match[1];
      }
    }
    return this.selectedFileUnreadableKey;
  }

  private get selectedFileUnreadableKey(): string {
    const name = this.selectedFile?.name.toLowerCase() ?? '';
    const match = UNREADABLE_KEYS_BY_EXTENSION.find(([extension]) =>
      name.endsWith(extension),
    );
    return match ? match[1] : 'cvImport.errors.unreadable';
  }
}
